import { r as registerInstance, h, a as Host, g as getElement, c as createEvent } from './index-I_gcXFWK.js';
import { H as HtmlUtil } from './html-util-C8DO915D.js';
import { T as TranslationService } from './translation.service-nljGIprK.js';
import { O as OntoTooltipPlacement } from './onto-tooltip-placement-D_J2-9CG.js';
import '@ontotext/workbench-api';

const ontoDialogCss = ".dialog-overlay{position:fixed;top:0;bottom:0;left:0;right:0;display:flex;align-items:center;justify-content:center;z-index:9998;margin-left:-10px;background-color:rgba(0, 0, 0, 0.5);font-family:var(--main-font), sans-serif;font-weight:var(--main-font-weight);color:var(--gw-secondary-base, var(--base-text-color))}.dialog{z-index:1001;max-width:60vw;max-height:90vh;font-family:inherit;font-weight:inherit;display:flex;flex-direction:column;position:relative;justify-content:space-between;border:none;color:var(--gw-dialog-color);background-color:var(--gw-dialog-background);box-shadow:0 5px 15px rgba(0, 0, 0, 0.5)}.dialog .dialog-header{display:flex;justify-content:space-between;padding:15px;font-size:20px}.dialog .dialog-header .dialog-title{margin:0.5rem 0;font-weight:400}.dialog .dialog-body{padding:0 15px 15px;overflow:auto}.dialog .dialog-footer{display:flex;justify-content:flex-end;padding:15px}";

const OntoDialog = class {
    constructor(hostRef) {
        registerInstance(this, hostRef);
        this.handleClose = (evt) => {
            this.config.onClose(evt);
        };
    }
    componentDidLoad() {
        this.documentOverflow = HtmlUtil.hideDocumentBodyOverflow();
        this.hostElement.addEventListener('keydown', this.preventLeavingDialog.bind(this));
        this.closeButton.focus();
    }
    disconnectedCallback() {
        this.hostElement.removeEventListener('keydown', this.preventLeavingDialog.bind(this));
        HtmlUtil.setDocumentBodyOverflow(this.documentOverflow);
    }
    render() {
        return (h(Host, { key: 'eeffeaac1aa5b80b89d0d176d89dc44291fd0cbb', tabindex: "-1" }, h("div", { key: '2ba8415fa038ad2666cc0b340a3ceba6f0919bc6', class: `dialog-overlay ${this.config.modalClass ? this.config.modalClass : ''}` }, h("dialog", { key: '39b9fc94944e12e8dcae98a65e74593dc173b336', class: "dialog" }, h("header", { key: '183327aad084ff6fd619d204a537871cace84684', class: "dialog-header" }, h("h3", { key: 'ae4c514e0f75e46d5787d284154392de808100ca', class: "dialog-title" }, this.config.dialogTitle)), h("main", { key: 'dc991f0ef3baa22f42f683528a4ca67ab8d5e7f4', class: "dialog-body" }, h("slot", { key: 'ddc1c5176d976f5a518fed1d2faa138d0bc5082a', name: "body" })), h("footer", { key: 'b3cb9449b54583a7b5489b7d56fa084e456b4d75', class: "dialog-footer" }, h("slot", { key: 'b3bc85bdfe12da62019be2e542737e5b6f7db4f6', name: "footer" }), h("button", { key: 'c58a84d33d8d07ae67a9988adaa320166371cc5b', class: "onto-btn onto-btn-primary", onClick: this.handleClose, ref: (el) => (this.closeButton = el) }, h("translate-label", { key: 'b593b5e113baa66e6878a2e885a46bc6581bf27f', labelKey: 'common.button.close' })))))));
    }
    preventLeavingDialog(ev) {
        HtmlUtil.preventLeavingDialog(this.hostElement, ev);
    }
    get hostElement() { return getElement(this); }
};
OntoDialog.style = ontoDialogCss;

class ObjectUtil {
    /**
     * Performs a deep equality check between two values.
     *
     * This function compares two values to determine if they are deeply equal.
     * It supports comparison of primitives, arrays, and objects, including nested structures.
     *
     * @param obj1 - The first value to compare.
     * @param obj2 - The second value to compare.
     * @returns `true` if both values are deeply equal, otherwise `false`.
     *
     * @example
     * // Comparing two deeply nested objects
     * const obj1 = { a: 1, b: { c: [1, 2, 3], d: { e: 'hello' } } };
     * const obj2 = { a: 1, b: { c: [1, 2, 3], d: { e: 'hello' } } };
     * console.log(deepEqual(obj1, obj2)); // true
     *
     * @example
     * // Comparing two arrays with different values
     * const arr1 = [1, 2, 3];
     * const arr2 = [1, 2, 4];
     * console.log(deepEqual(arr1, arr2)); // false
     */
    static deepEqual(obj1, obj2) {
        // Check if both values are strictly equal (handles primitives and references)
        if (obj1 === obj2) {
            return true;
        }
        // Check if either value is null or undefined
        if (obj1 === null || obj1 === undefined || obj2 === null || obj2 === undefined) {
            return false;
        }
        // Check if both values are objects
        if (typeof obj1 === 'object' && typeof obj2 === 'object') {
            // Ensure both have the same constructor (handles different types of objects, like Date, RegExp)
            if (obj1.constructor !== obj2.constructor) {
                return false;
            }
            // Handle arrays
            if (Array.isArray(obj1) && Array.isArray(obj2)) {
                // Check if both arrays have the same length
                if (obj1.length !== obj2.length) {
                    return false;
                }
                // Compare each element recursively
                for (let i = 0; i < obj1.length; i++) {
                    if (!ObjectUtil.deepEqual(obj1[i], obj2[i])) {
                        return false;
                    }
                }
                return true;
            }
            // Handle plain objects
            if (!Array.isArray(obj1) && !Array.isArray(obj2)) {
                const keys1 = Object.keys(obj1);
                const keys2 = Object.keys(obj2);
                // Check if both objects have the same number of keys
                if (keys1.length !== keys2.length) {
                    return false;
                }
                // Check if all keys and values are deeply equal
                for (const key of keys1) {
                    if (!(key in obj2) || // Ensure the key exists in obj2
                        !ObjectUtil.deepEqual(obj1[key], obj2[key])) {
                        return false;
                    }
                }
                return true;
            }
            // If one is an array and the other is not, they are not equal
            return false;
        }
        // Default case: Not equal
        return false;
    }
    /**
     * Creates a deep copy of the given object.
     *
     * This method recursively copies all properties and values of the object,
     * handling different types such as primitives, arrays, plain objects, and built-in types.
     * It ensures that the copied object does not share references with the original object,
     * meaning that all nested structures are also copied independently.
     *
     * Special handling:
     * - Objects with a `copy()` method will use that method for cloning
     * - Date objects are cloned preserving their time value
     * - RegExp objects are cloned with their pattern and flags
     * - Map and Set objects are deeply cloned with their contents
     * - Arrays are deeply cloned with all elements
     * - Plain objects are recursively cloned
     *
     * @param obj - The object to be deeply copied. Can be any type, including primitives,
     *              arrays, objects, or built-in types.
     * @returns A deep copy of the object. If the input is a primitive, null, undefined,
     *          or a function, the original value is returned as-is.
     */
    static deepCopy(obj) {
        // Handle primitives, null, undefined, and functions
        if (typeof obj !== 'object' || obj === null || obj === undefined || typeof obj === 'function') {
            return obj;
        }
        // Check if object has a custom copy method
        if (ObjectUtil.hasCopyMethod(obj)) {
            return obj.copy();
        }
        // Try to handle built-in types (including arrays)
        const builtInCopy = ObjectUtil.copyBuiltInType(obj);
        if (builtInCopy !== null) {
            return builtInCopy;
        }
        // Handle plain objects or objects with a prototype
        return ObjectUtil.copyPlainObject(obj);
    }
    /**
     * Attempts to copy built-in types like Array, Date, RegExp, Map, and Set.
     * @param obj - The object to copy.
     * @returns A copy of the object if it's a built-in type, or null if it's not a recognized built-in type.
     */
    static copyBuiltInType(obj) {
        // Handle arrays
        if (Array.isArray(obj)) {
            return obj.map(item => ObjectUtil.deepCopy(item));
        }
        // Handle Date objects
        if (obj instanceof Date) {
            return new Date(obj.getTime());
        }
        // Handle RegExp objects
        if (obj instanceof RegExp) {
            return new RegExp(obj.source, obj.flags);
        }
        // Handle Map objects
        if (obj instanceof Map) {
            return new Map(Array.from(obj.entries()).map(([key, value]) => [
                ObjectUtil.deepCopy(key),
                ObjectUtil.deepCopy(value)
            ]));
        }
        // Handle Set objects
        if (obj instanceof Set) {
            return new Set(Array.from(obj.values()).map(value => ObjectUtil.deepCopy(value)));
        }
        return null;
    }
    /**
     * Creates a deep copy of a plain object.
     * @param obj - The object to copy.
     * @returns A deep copy of the object.
     */
    static copyPlainObject(obj) {
        const result = Object.create(Object.getPrototypeOf(obj));
        // Recursively copy each key-value pair
        for (const key of Object.keys(obj)) {
            result[key] = ObjectUtil.deepCopy(obj[key]);
        }
        return result;
    }
    /**
     * Type guard to check if the value has a 'copy' method.
     * @param value - The value to check.
     * @returns True if the value has a 'copy' method, false otherwise.
     */
    static hasCopyMethod(value) {
        return typeof value === 'object' && value !== null && Object.prototype.hasOwnProperty.call(value, 'copy') && typeof value.copy === 'function';
    }
}
/**
 * Checks if the given object is null or undefined.
 * @param obj - The object to check.
 * @returns True if the object is null or undefined, false otherwise.
 */
ObjectUtil.isNullOrUndefined = (obj) => {
    return obj === null || obj === undefined;
};

/**
 * Base class for all models, providing deep copy functionality.
 *
 * This abstract class ensures that any model extending it implements immutability
 * by providing a `copy` method, which creates a deep copy of the current instance.
 *
 * All models in the application are expected to extend this class to inherit the
 * `copy` behavior, ensuring consistency in how objects are cloned and manipulated.
 *
 * @template <T> The type of the model extending this class.
 */
class Model {
    /**
     * Creates a deep copy of the current instance.
     *
     * This method utilizes `ObjectUtil.deepCopy` to create a new instance of the model
     * with all properties and nested objects deeply cloned, ensuring that any changes
     * to the copy do not affect the original instance.
     *
     * @returns A new instance of the type `T` that is a deep copy of the original.
     */
    copy() {
        return ObjectUtil.deepCopy(this);
    }
}

/**
 * Abstract base class for managing a list of items that extends the {@link Model} class.
 *
 * This class provides common utility methods for manipulating and querying the list of items,
 * such as sorting, filtering, and finding elements. Subclasses can extend this class to work
 * with specific types of models.
 *
 * @template T - The type of items in the list.
 */
class ModelList extends Model {
    /**
     * Creates an instance of the {@link ModelList}.
     *
     * @param items - An optional array of items to initialize the list. If no items are provided,
     *                an empty array is used by default.
     */
    constructor(items = []) {
        super();
        this.items = items;
    }
    /**
     * Returns the number of items in the list.
     *
     * @returns The count of items in the list.
     */
    size() {
        return this.items.length;
    }
    /**
     * Retrieves all items in the list.
     *
     * @returns An array containing all items in the list.
     */
    getItems() {
        return this.items;
    }
    /**
     * Retrieves the first item in the list.
     *
     * @returns The first item in the list, or `undefined` if the list is empty.
     */
    getFirstItem() {
        return this.items[0];
    }
    /**
     * Sorts the items in place using the specified comparator function.
     *
     * @param comparator - A comparison function that defines the sort order.
     *                     Should return a negative number if `a` < `b`,
     *                     zero if `a` === `b`, or a positive number if `a` > `b`.
     *
     * @example
     * const modelList = new MyModelList([{ id: 2 }, { id: 1 }]);
     * modelList.sort((a, b) => a.id - b.id);
     * console.log(modelList.items); // Outputs: [{ id: 1 }, { id: 2 }]
     */
    sort(comparator) {
        this.items.sort(comparator);
    }
    /**
     * Filters the items using the specified filter function.
     *
     * @param filterFunction - A function that returns `true` for items to include
     *                         and `false` for items to exclude.
     * @returns A new array of items that match the filter criteria.
     */
    filter(filterFunction) {
        return this.items.filter(filterFunction);
    }
    /**
     * Finds an item in the list that matches the specified filter function.
     *
     * @param filterFunction - A function that returns `true` for the desired item.
     * @returns The first item that matches the filter criteria, or `undefined` if none match.
     */
    find(filterFunction) {
        return this.items.find(filterFunction);
    }
    /**
     * Checks if the collection of items is empty.
     *
     * @returns {boolean} - Returns `true` if there are no items in the collection, otherwise `false`.
     */
    isEmpty() {
        return this.items.length === 0;
    }
    /**
     * Adds a new item to the list in the first position.
     *
     * @param item - The item to add.
     */
    addToStart(item) {
        this.items.unshift(item);
    }
    /**
     * Adds an array of items to the end of the list.
     *
     * @param items the items to add
     */
    addItems(items) {
        this.items.push(...items);
    }
    /**
     * Removes a specific item from the list.
     *
     * This method finds the first occurrence of the specified item in the list and removes it.
     * If the item is not found in the list, no changes are made.
     *
     * @param item - The item to be removed from the list.
     */
    remove(item) {
        const index = this.items.indexOf(item);
        if (index !== -1) {
            this.items.splice(index, 1);
        }
    }
}

/**
 * Represents a list of subscriptions that can be managed collectively.
 * Extends the {@link ModelList} class, specialized for {@link Subscription} type.
 */
class SubscriptionList extends ModelList {
    /**
     * Creates a new instance of SubscriptionList.
     * @param subscriptions - An optional array of Subscription functions to initialize the list.
     */
    constructor(subscriptions) {
        super(subscriptions);
    }
    /**
     * Adds a new subscription to the list.
     * @param subscription - The Subscription function to be added to the list.
     */
    add(subscription) {
        this.items.push(subscription);
    }
    /**
     * Adds multiple subscriptions to the list.
     * @param subscriptions - An array of Subscription functions to be added.
     */
    addAll(subscriptions) {
        this.items.push(...subscriptions);
    }
    /**
     * Calls all subscription functions in the list and then clears the list. Calling a subscription
     * function unsubscribes the subscription. This effectively unsubscribes all subscriptions
     * and removes them from the list.
     */
    unsubscribeAll() {
        this.items.forEach(subscription => subscription());
        this.items = [];
    }
}

const ontoToggleSwitchCss = ".toggle-switch{all:unset;clear:both;margin:0 5px;width:36px}.toggle-switch:focus{outline:none;box-shadow:none}.toggle-switch:focus-visible{outline:none;box-shadow:none}.toggle-switch input:empty{display:none}.toggle-switch input:empty~label{position:relative;cursor:pointer;user-select:none;margin:0.05em 0.6em -0.05em 0;vertical-align:text-bottom}.toggle-switch input:empty~label:before{display:block;content:\" \";width:2.2em;height:1.25em;background-color:var(--gw-toggleswitch-background);border-radius:10em;transition:all 100ms ease-in}.toggle-switch input:empty~label:after{position:absolute;display:block;top:0.08em;left:0.1em;content:\" \";width:0.9em;height:0.9em;background-color:var(--gw-toggleswitch-handle-background);border:1px solid var(--gw-toggleswitch-background);border-radius:50%;transition:all 100ms ease-in}.toggle-switch input:checked~label:before{background-color:var(--gw-toggleswitch-checked-background)}.toggle-switch input:checked~label:after{left:1em;background-color:var(--gw-toggleswitch-handle-checked-background);border:1px solid var(--gw-toggleswitch-checked-background)}";

const OntoToggleSwitch = class {
    constructor(hostRef) {
        registerInstance(this, hostRef);
        this.toggleChanged = createEvent(this, "toggleChanged");
        this.subscriptions = new SubscriptionList();
        /**
         * Determines whether the toggle switch is checked or not.
         */
        this.checked = false;
        this.toggle = () => {
            this.checked = !this.checked;
            this.toggleChanged.emit({ checked: this.checked, context: this.context });
        };
    }
    subscribeToLanguageChange() {
        if (this.tooltipTranslationKey) {
            this.subscriptions.add(TranslationService.onTranslate(this.tooltipTranslationKey, [], (translation) => {
                this.tooltipLabel = translation;
            }));
        }
    }
    disconnectedCallback() {
        this.subscriptions.unsubscribeAll();
    }
    connectedCallback() {
        this.subscribeToLanguageChange();
    }
    render() {
        return (h("section", { key: 'd9d7433b0d5d86830bc124483a3982f255ea3851' }, this.labelKey &&
            h("label", { key: '30e1f9c5a47b7a17f614ed2c31c191e4364f0552' }, h("strong", { key: 'e573b14579f77efd38636093143519451042e386' }, h("translate-label", { key: '1653cbf17a0cd2f57e9011ed7bf3acc589ae9bde', labelKey: this.labelKey }), "\u00A0")), h("button", { key: '3ff359d2c065db8653f12233d69934483c00b0d8', class: "toggle-switch", onClick: this.toggle, "tooltip-append-to": "parent", "tooltip-content": this.tooltipLabel, "tooltip-placement": OntoTooltipPlacement.TOP }, h("input", { key: 'c36ab591c99d5030ab4528774bd8226ecacb855e', type: "checkbox", checked: this.checked }), h("label", { key: '86c93d3ccebd7f1324b6d39192472464580f1120' }))));
    }
};
OntoToggleSwitch.style = ontoToggleSwitchCss;

export { OntoDialog as onto_dialog, OntoToggleSwitch as onto_toggle_switch };
